import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { errMsg } from "@/lib/api";
import { usePrefs } from "@/lib/context";
import { fetcher, useCategories, downloadCsv } from "@/lib/hooks";
import { PageHeader, Money } from "@/components/common";
import { Stars } from "@/components/Reviews";

const EXPORTS = [["loyers", "leases"], ["tickets", "tickets"], ["factures", "invoices"], ["charges", "commonCharges"], ["contrats", "contracts"]];

function Satisfaction({ a }) {
  const { t } = usePrefs();
  const mMax = 5;
  return (
    <section className="mt-6 rounded-sm border border-slate-200 bg-white p-6" data-testid="satisfaction-section">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-display text-lg font-bold">{t("satisfaction")}</h3>
        {a.reviews_low_open > 0 && <span className="rounded-sm bg-rose-100 px-2 py-0.5 text-[11px] font-bold text-rose-800" data-testid="satisfaction-low-open">{a.reviews_low_open} {t("openLow")}</span>}
        {a.reviews_avg != null && <span className="flex items-center gap-2 text-sm" data-testid="satisfaction-average"><Stars value={Math.round(a.reviews_avg)} size={14} testId="satisfaction-stars" /><span className="font-data font-bold">{a.reviews_avg.toFixed(1)}/5</span></span>}
      </div>
      {!a.reviews_by_provider.length ? <p className="text-sm text-slate-500">{t("noReviews")}</p> : (
        <div className="grid gap-8 lg:grid-cols-2">
          <div>
            <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">{t("byProvider")}</div>
            <table className="w-full text-sm" data-testid="satisfaction-by-provider">
              <thead><tr className="border-b text-start text-[10px] uppercase tracking-wider text-slate-500"><th className="py-2 text-start">{t("role_prestataire")}</th><th className="text-start">{t("averageRating")}</th><th className="text-end">{t("reviews")}</th><th className="text-end">{t("lowRatings")}</th></tr></thead>
              <tbody>{a.reviews_by_provider.map((p) => (
                <tr key={p.provider_id || p.provider_name} className="border-b border-slate-100" data-testid={`satisfaction-provider-${p.provider_id}`}>
                  <td className="py-2 font-semibold">{p.provider_name}</td>
                  <td><span className="flex items-center gap-1.5"><Stars value={Math.round(p.avg)} size={12} testId={`sat-stars-${p.provider_id}`} /><span className="font-data text-xs">{p.avg.toFixed(1)}</span></span></td>
                  <td className="text-end font-data">{p.count}</td>
                  <td className={`text-end font-data ${p.low ? "font-bold text-rose-700" : "text-slate-400"}`}>{p.low}</td>
                </tr>))}
              </tbody>
            </table>
          </div>
          <div>
            <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">{t("byMonth")}</div>
            <div className="flex h-44 items-end gap-3" data-testid="satisfaction-by-month">
              {a.reviews_by_month.map((m) => (
                <div key={m.month} className="flex flex-1 flex-col items-center gap-1" title={`${m.avg}/5 · ${m.count}`}>
                  <span className="font-data text-[10px] font-bold">{m.avg.toFixed(1)}</span>
                  <div className={`w-full max-w-[40px] rounded-t-sm ${m.avg < 3 ? "bg-rose-500" : "bg-[#B8860B]"}`} style={{ height: `${(130 * m.avg) / mMax}px` }} />
                  <span className="font-data text-[10px] text-slate-500">{m.month.slice(2)}</span>
                  <span className="text-[9px] text-slate-400">{m.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function Stat({ label, value, testId }) {
  return <div className="rounded-sm border border-slate-200 bg-white p-5" data-testid={testId}><div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</div><div className="mt-2 font-data text-2xl">{value ?? "—"}</div></div>;
}

export default function Reports() {
  const { t } = usePrefs();
  const { label } = useCategories();
  const { data: a } = useQuery({ queryKey: ["analytics"], queryFn: () => fetcher("/analytics") });
  const max = Math.max(1, ...(a?.rent_by_month || []).map((m) => m.due));
  const catMax = Math.max(1, ...(a?.by_category || []).map((c) => c[1]));
  const dl = (k) => downloadCsv(k).catch((e) => toast.error(errMsg(e)));
  return (
    <div data-testid="reports-page">
      <PageHeader eyebrow={t("secFinance")} title={t("reports")} />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label={t("avgResolution")} value={a?.avg_resolution_hours != null ? `${a.avg_resolution_hours} h` : null} testId="stat-resolution" />
        <Stat label={t("slaCompliance")} value={a?.sla_compliance != null ? `${a.sla_compliance}%` : null} testId="stat-sla" />
        <Stat label={t("escalated")} value={a?.escalated_open} testId="stat-escalated" />
        <Stat label={t("recurring")} value={a?.recurring?.length} testId="stat-recurring" />
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <section className="rounded-sm border border-slate-200 bg-white p-6" data-testid="rent-by-month">
          <h3 className="mb-1 font-display text-lg font-bold">{t("rentByMonth")}</h3>
          <div className="mb-5 flex gap-4 text-[11px] text-slate-500"><span><span className="mr-1 inline-block h-2 w-2 bg-slate-300" />{t("due_s")}</span><span><span className="mr-1 inline-block h-2 w-2 bg-[#B8860B]" />{t("collected")}</span></div>
          <div className="flex h-56 items-end gap-3">
            {(a?.rent_by_month || []).map((m) => (
              <div key={m.month} className="flex flex-1 flex-col items-center gap-1" title={`${m.collected} / ${m.due} EUR`}>
                <div className="relative w-full max-w-[44px] rounded-t-sm bg-slate-200" style={{ height: `${(180 * m.due) / max}px` }}>
                  <div className="absolute bottom-0 w-full rounded-t-sm bg-[#B8860B] transition-all duration-700" style={{ height: `${(180 * m.collected) / max}px` }} />
                </div>
                <span className="font-data text-[10px] text-slate-500">{m.month.slice(2)}</span>
              </div>
            ))}
          </div>
          {a?.rent_by_month?.length > 0 && <div className="mt-3 text-xs text-slate-500">Total : <Money amount={a.rent_by_month.reduce((s, m) => s + m.collected, 0)} currency="EUR" showContract={false} /></div>}
        </section>
        <section className="rounded-sm border border-slate-200 bg-white p-6" data-testid="tickets-by-category">
          <h3 className="mb-4 font-display text-lg font-bold">{t("byCategory")}</h3>
          <div className="space-y-2.5">
            {(a?.by_category || []).map(([k, n]) => (
              <div key={k} className="grid grid-cols-[120px_1fr_28px] items-center gap-3 text-sm">
                <span className="truncate text-slate-600">{label(k)}</span>
                <div className="h-2 rounded-full bg-slate-100"><div className="h-full rounded-full bg-[#0F172A]" style={{ width: `${(100 * n) / catMax}%` }} /></div>
                <span className="text-right font-data text-xs">{n}</span>
              </div>
            ))}
          </div>
        </section>
      </div>
      {a?.reviews_by_provider && <Satisfaction a={a} />}
      <section className="mt-6 rounded-sm border border-slate-200 bg-white p-6">
        <h3 className="mb-4 font-display text-lg font-bold">{t("export")}</h3>
        <div className="flex flex-wrap gap-2">
          {[...EXPORTS, ...(a?.reviews_by_provider ? [["avis", "exportReviews"], ["satisfaction", "exportSatisfaction"], ["avis_negatifs", "exportLowReviews"]] : [])].map(([k, l]) => (
            <button key={k} onClick={() => dl(k)} data-testid={`export-${k}`} className="flex items-center gap-2 rounded-sm border border-slate-300 px-4 py-2 text-sm font-semibold transition-colors hover:border-[#0F172A] hover:bg-[#0F172A] hover:text-white">
              <Download size={14} />{t(l)}
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
